import React, { useState, useEffect } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import {
    LayoutDashboard,
    LogOut,
    PlusCircle,
    Cog,
    ArrowLeft,
    FileText,
    Star,
    CheckCircle,
    XCircle,
    Download,
    Search,
    Mail,
    Phone,
    Linkedin,
    AlertTriangle
} from "lucide-react";
import { motion } from "framer-motion";
import API_BASE_URL from "./apiConfig";
import RecruiterSidebar from "./components/RecruiterSidebar";

const CandidateReport = () => {
    const { rankingId } = useParams();
    const navigate = useNavigate();
    const [recruiterData, setRecruiterData] = useState(null);
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const storedUser = localStorage.getItem("recruiterUser");
        if (storedUser) {
            setRecruiterData(JSON.parse(storedUser));
            fetchReport(rankingId);
        } else {
            navigate("/recruiter/signin");
        }
    }, [rankingId, navigate]);

    const fetchReport = async (id) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/rankings/${id}/report`);
            if (!response.ok) throw new Error("Failed to fetch report");
            const data = await response.json();
            setReport(data);
        } catch (error) {
            console.error("Error fetching report:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("recruiterUser");
        navigate("/recruiter/signin");
    };

    const handleDownloadReport = async () => {
        if (!report || !report.evaluationId) {
            alert("Report not yet generated completely.");
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/rankings/evaluations/${report.evaluationId}/download`);
            if (!response.ok) throw new Error("Failed to download report");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${report.candidateName.replace(' ', '_')}_Evaluation_Report.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading report:", error);
            alert("Failed to download report.");
        }
    };

    const handleViewCV = async () => {
        if (report && report.sessionId) {
            try {
                const response = await fetch(`${API_BASE_URL}/interview/cv-file/${report.sessionId}`);
                if (!response.ok) {
                    if (response.status === 404) {
                        alert("CV file not found on the server (it may have been deleted).");
                    } else {
                        alert("Failed to load CV file.");
                    }
                    return;
                }
                const blob = await response.blob();
                const fileURL = URL.createObjectURL(blob);
                window.open(fileURL, "_blank");
            } catch (err) {
                console.error("Error viewing CV:", err);
                alert("Failed to access the CV file.");
            }
        } else {
            alert("CV not available for this session.");
        }
    };

    // Clean up markdown bullet points from Gemini output
    const formatListElements = (textStr) => {
        if (!textStr) return [];
        let items = [];
        if (Array.isArray(textStr)) {
            items = textStr;
        } else {
            items = [textStr];
        }

        return items
            // If any string item contains inline asterisk bullets, split them up
            .flatMap(s => typeof s === 'string' ? s.replace(/([^\n])(\*\s)/g, '$1\n$2').split('\n') : [s])
            .map(s => typeof s === 'string' ? s.replace(/^[-*]\s*/, '').replace(/\*\*/g, '').trim() : '')
            .filter(s => s.length > 3 && !s.toLowerCase().startsWith("strength") && !s.toLowerCase().startsWith("weakness"));
    };

    const formatDetailedAnalysis = (text) => {
        if (!text) return "Analysis securely generated by AI based on candidate's combined technical and communication capabilities.";

        let formattedStr = text
            .replace(/(^|\n)\d+\.\s*(Strengths:|Weaknesses:|Areas for Improvement:|Areas of Improvement:|Hiring Recommendation:)/gi, '$1$2')
            .replace(/([^\n])(\*|- )/g, '$1\n$2') // Ensure bullets are on new lines
            .replace(/\*\*/g, ''); // Remove markdown bold asterisks

        const lines = formattedStr.split('\n');
        const elements = [];

        for (let idx = 0; idx < lines.length; idx++) {
            const line = lines[idx];
            const trimmed = line.trim();
            if (!trimmed) continue;
            if (trimmed.toLowerCase().includes('candidate evaluation report')) continue;

            const lowerCleaned = trimmed.toLowerCase();

            // Check for Hiring Recommendation
            if (lowerCleaned.startsWith('hiring recommendation')) {
                const parts = trimmed.split(':');
                const label = parts[0].trim();
                let fullValue = parts.slice(1).join(':').trim();

                // Consume all remaining lines into the explanation until the end (or another major section starts)
                while (idx + 1 < lines.length) {
                    let nextLine = lines[idx + 1].trim();
                    if (nextLine && nextLine.toLowerCase().match(/^(strength|weakness|area)/)) {
                        break;
                    }
                    if (nextLine) {
                        fullValue += (fullValue ? "\n\n" : "") + nextLine.replace(/^[-*]\s*/, '').trim();
                    }
                    idx++; // Advance index to skip processing this line normally
                }

                if (!fullValue) fullValue = "Pending";

                // Extract status word
                let status = "Consider";
                let bgClass = "bg-blue-50", borderClass = "border-blue-200", textClass = "text-blue-700", badgeBgClass = "bg-blue-600";

                const valLower = fullValue.toLowerCase();

                // Keep the entire badge string cleaner by pulling out the main outcome
                if (valLower.includes("strong hire") || valLower.includes("strongly")) {
                    status = "Strong Hire";
                    bgClass = "bg-green-50"; borderClass = "border-green-300"; textClass = "text-green-800"; badgeBgClass = "bg-green-600";
                } else if (valLower.includes("reject") || valLower.includes("not recommend")) {
                    status = "Reject";
                    bgClass = "bg-red-50"; borderClass = "border-red-200"; textClass = "text-red-800"; badgeBgClass = "bg-red-600";
                } else if (valLower.includes("hire") || valLower.includes("recommend")) {
                    status = "Hire";
                    bgClass = "bg-emerald-50"; borderClass = "border-emerald-200"; textClass = "text-emerald-800"; badgeBgClass = "bg-emerald-500";
                } else if (valLower.includes("consider")) {
                    status = "Consider";
                    bgClass = "bg-amber-50"; borderClass = "border-amber-200"; textClass = "text-amber-800"; badgeBgClass = "bg-amber-500";
                }

                // Remove the exact status word from the explanation to avoid redundancy, but keep the rest
                let explanation = fullValue;
                const exactStatusRegex = new RegExp(`(^|\\n|\\s)${status}[\\.\\s-]*$`, "i");
                if (exactStatusRegex.test(explanation)) {
                    // Try to strip it from the end if it's there
                    explanation = explanation.replace(new RegExp(`(^|\\n|\\s)${status}[\\.\\s-]*$`, "i"), "").trim();
                }

                elements.push(
                    <div key={`hr-${idx}`} className={`my-6 p-5 rounded-2xl border ${bgClass} ${borderClass} shadow-sm`}>
                        <div className="flex items-center justify-between mb-2">
                            <span className={`font-bold text-lg ${textClass}`}>{label}:</span>
                            <span className={`px-5 py-2 rounded-xl text-white font-black shadow-sm ${badgeBgClass}`}>
                                {status}
                            </span>
                        </div>
                        {explanation && (
                            <div className={`mt-3 ${textClass} opacity-90 leading-relaxed text-md`}>
                                {explanation.split("\n\n").map((para, i) => <p key={i} className={i > 0 ? "mt-2" : ""}>{para}</p>)}
                            </div>
                        )}
                    </div>
                );
                continue;
            }

            if (lowerCleaned.startsWith('strength') || lowerCleaned.startsWith('weakness') || lowerCleaned.startsWith('areas for improvement') || lowerCleaned.startsWith('areas of improvement')) {
                elements.push(<h5 key={idx} className="font-bold text-gray-800 mt-5 mb-3 text-lg border-b border-gray-100 pb-2">{trimmed}</h5>);
                continue;
            }
            if (trimmed.startsWith('*') || trimmed.startsWith('-')) {
                elements.push(
                    <div key={idx} className="flex gap-3 ml-2 mb-2 items-start text-gray-600">
                        <span className="text-blue-500 mt-1 text-lg leading-none">•</span>
                        <span className="leading-relaxed">{trimmed.substring(1).trim()}</span>
                    </div>
                );
                continue;
            }
            elements.push(<p key={idx} className="mb-3 text-gray-600 leading-relaxed indent-1">{trimmed}</p>);
        }

        return elements;
    };

    if (!recruiterData) return null;

    return (
        <div className="h-screen w-screen flex bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden fixed inset-0">
            {/* Sidebar (Same as Dashboard) */}
            <RecruiterSidebar />

            {/* Main Content */}
            <main className="flex-1 h-screen flex flex-col overflow-hidden py-8 px-10">
                {/* Header with Back Button */}
                <div className="mb-6 flex-shrink-0 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-white hover:shadow-md rounded-full transition-all text-gray-600"
                        >
                            <ArrowLeft className="w-6 h-6" />
                        </button>
                        <div>
                            <h2 className="text-3xl font-bold text-gray-800">Candidate Evaluation Report</h2>
                            <p className="text-gray-500 mt-1">Detailed performance analysis and AI insights</p>
                        </div>
                    </div>

                    <button
                        onClick={handleDownloadReport}
                        className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg"
                    >
                        <Download className="w-5 h-5" /> Download PDF
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : !report ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                        <FileText className="w-16 h-16 mb-4 opacity-20" />
                        <p className="text-lg">Report not found</p>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto space-y-6 pr-2 custom-scrollbar">
                        {/* Summary Header Card */}
                        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                            <div className="bg-gradient-to-r from-blue-900 to-indigo-900 p-8 text-white">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-4xl font-bold">{report.candidateName}</h3>
                                            <span className="bg-blue-500/30 px-4 py-1 rounded-full text-sm font-bold backdrop-blur-md">Rank #{report.rank}</span>
                                        </div>
                                        <p className="text-blue-100 text-xl">{report.position}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-5xl font-black">{report.score}</p>
                                        <p className="text-blue-200 font-bold uppercase tracking-wider text-sm mt-1">Overall Match Score</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-4 divide-x divide-gray-100 border-b border-gray-100 bg-white">
                                <div className="p-6 text-center">
                                    <p className="text-gray-500 text-sm font-bold uppercase mb-1">CV Match</p>
                                    <p className="text-2xl font-black text-blue-600">{report.cvScore}</p>
                                </div>
                                <div className="p-6 text-center">
                                    <p className="text-gray-500 text-sm font-bold uppercase mb-1">Interview Score</p>
                                    <p className="text-2xl font-black text-indigo-600">{report.interviewScore}</p>
                                </div>
                                <div className="p-6 text-center">
                                    <p className="text-gray-500 text-sm font-bold uppercase mb-1">Soft Skills</p>
                                    <p className="text-2xl font-black text-purple-600">{report.facialRecognitionScore}</p>
                                </div>
                                <div className="p-6 text-center">
                                    <p className="text-gray-500 text-sm font-bold uppercase mb-1">Status</p>
                                    <span className={`inline-block mt-1 px-4 py-1 rounded-full text-sm font-bold ${report.interviewStatus === 'Selected' || report.interviewStatus === 'Shortlisted' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                        {report.interviewStatus}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {report.manualEndDetected && (
                            <div className="bg-orange-50 border-l-4 border-orange-500 p-5 rounded-2xl flex gap-3 shadow-sm items-center">
                                <AlertTriangle className="w-6 h-6 text-orange-500 shrink-0" />
                                <div>
                                    <h5 className="font-bold text-orange-800">Interview Ended Early</h5>
                                    <p className="text-orange-700 text-sm">The candidate manually ended the interview before completion. This triggers an automatic 15% penalty on their final interview score.</p>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-3 gap-6">
                            {/* Skills Breakdown */}
                            <div className="col-span-2 space-y-6">
                                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                                    <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <Star className="w-5 h-5 text-yellow-500" /> Skill Proficiency
                                    </h4>
                                    <div className="grid grid-cols-1 gap-6">
                                        {report.skills.map((skill, index) => (
                                            <div key={index} className="space-y-2">
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="font-bold text-gray-700">{skill.name}</span>
                                                    <span className="font-black text-blue-600">{skill.percentage}%</span>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-3">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${skill.percentage}%` }}
                                                        transition={{ duration: 1, delay: index * 0.1 }}
                                                        className={`h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 shadow-sm shadow-blue-200`}
                                                    ></motion.div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Detailed AI Analysis */}
                                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                                    <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-blue-600" /> AI Insights & Recommendations
                                    </h4>
                                    <div className="text-gray-600 font-medium">
                                        {formatDetailedAnalysis(report.summary || report.detailedAnalysis)}
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar Info - Strengths & Weaknesses */}
                            <div className="space-y-6">
                                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                                    <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5 text-green-500" /> Key Strengths
                                    </h4>
                                    <ul className="space-y-4">
                                        {formatListElements(report.strengths).map((strength, i) => (
                                            <li key={i} className="flex gap-3 text-gray-600 bg-green-50/50 p-3 rounded-xl border border-green-100/50">
                                                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                                <span className="text-sm font-semibold">{strength}</span>
                                            </li>
                                        ))}
                                        {formatListElements(report.strengths).length === 0 && (
                                            <p className="text-gray-400 text-sm">No specific strengths highlighted.</p>
                                        )}
                                    </ul>
                                </div>

                                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                                    <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <XCircle className="w-5 h-5 text-red-500" /> Areas of Improvement
                                    </h4>
                                    <ul className="space-y-4">
                                        {formatListElements(report.weaknesses).map((weakness, i) => (
                                            <li key={i} className="flex gap-3 text-gray-600 bg-red-50/50 p-3 rounded-xl border border-red-100/50">
                                                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                                <span className="text-sm font-semibold">{weakness}</span>
                                            </li>
                                        ))}
                                        {formatListElements(report.weaknesses).length === 0 && (
                                            <p className="text-gray-400 text-sm">No specific areas of improvement highlighted.</p>
                                        )}
                                    </ul>
                                </div>

                                {/* Candidate Details */}
                                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                                    <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">Contact Details</h4>
                                    <div className="space-y-4">
                                        {report.email ? (
                                            <div className="flex items-center gap-3 text-gray-600">
                                                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                                    <Mail className="w-4 h-4" />
                                                </div>
                                                <a href={`mailto:${report.email}`} className="text-sm hover:text-blue-600 hover:underline">{report.email}</a>
                                            </div>
                                        ) : null}
                                        {report.phone ? (
                                            <div className="flex items-center gap-3 text-gray-600">
                                                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                                    <Phone className="w-4 h-4" />
                                                </div>
                                                <a href={`tel:${report.phone}`} className="text-sm hover:text-blue-600 hover:underline">{report.phone}</a>
                                            </div>
                                        ) : null}
                                        {report.linkedin ? (
                                            <div className="flex items-center gap-3 text-gray-600 overflow-hidden">
                                                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg shrink-0">
                                                    <Linkedin className="w-4 h-4" />
                                                </div>
                                                <a href={report.linkedin} target="_blank" rel="noreferrer" className="text-sm hover:text-blue-600 hover:underline truncate">{report.linkedin}</a>
                                            </div>
                                        ) : null}
                                        {!report.email && !report.phone && !report.linkedin && (
                                            <p className="text-sm text-gray-400">No contact details provided.</p>
                                        )}
                                    </div>
                                </div>

                                {/* CV Preview Button */}
                                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                                    <h4 className="font-bold text-gray-800 mb-4">Original Resume</h4>
                                    <button
                                        onClick={handleViewCV}
                                        className="w-full flex items-center justify-center gap-2 bg-gray-50 border border-gray-200 text-gray-700 py-3 rounded-xl hover:bg-white hover:shadow-md transition-all font-semibold"
                                    >
                                        <FileText className="w-5 h-5" /> View PDF Resume
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default CandidateReport;
